// audio-engine.js — playback over HTMLAudioElement.
//
// Mirrors the responsibilities of KFE.Core/Audio/AudioManager.cs:
//   • Resolve a URL via PlatformServices.media (see media-paths.js)
//   • Play, pause, seek, set volume, set rate
//   • Emit "ended" via Promise resolution and via DOM events
//   • Lyric sync: callback fires on every `timeupdate` with the current sec
//
// iOS Safari notes:
//   • The first play() must be called inside a user gesture; we don't try to
//     work around that here, the calling screen is responsible.
//   • playbackRate works on iOS for HTMLAudioElement.

import { resolveAudio } from "../data/media-paths.js";

export class AudioEngine extends EventTarget {
  constructor() {
    super();
    this.el = new Audio();
    this.el.preload = "auto";
    this.el.crossOrigin = "anonymous";
    this.el.addEventListener("ended", () => this.dispatchEvent(new Event("ended")));
    this.el.addEventListener("timeupdate", () => {
      this.dispatchEvent(new CustomEvent("time", { detail: this.el.currentTime }));
    });
    this.el.addEventListener("error", (e) => {
      this.dispatchEvent(new CustomEvent("error", { detail: this.el.error }));
    });
    /** @type {AbortController | null} */ this._currentToken = null;
  }

  /**
   * Play an audio file, identified by an MMU model or by an explicit URL.
   * @param {{ url?: string, audioStem?: string, audioFileName?: string }} target
   * @param {{ volume?: number, rate?: number }} [options]
   * @returns {Promise<void>} resolves on `ended`, rejects on `error`
   */
  async play(target, options = {}) {
    if (this._currentToken) this._currentToken.abort();
    const token = (this._currentToken = new AbortController());

    let url = target.url;
    if (!url) url = await resolveAudio(target);
    if (!url) throw new Error("[audio] could not resolve audio for " + JSON.stringify(target));

    this.el.src = url;
    this.el.volume = options.volume ?? 1;
    this.el.playbackRate = options.rate ?? 1;
    this.el.currentTime = 0;

    return new Promise((resolve, reject) => {
      const onEnd = () => { cleanup(); resolve(); };
      const onErr = () => { cleanup(); reject(this.el.error); };
      const onAbort = () => { cleanup(); resolve(); /* superseded play */ };
      const cleanup = () => {
        this.el.removeEventListener("ended", onEnd);
        this.el.removeEventListener("error", onErr);
        token.signal.removeEventListener("abort", onAbort);
      };
      this.el.addEventListener("ended", onEnd, { once: true });
      this.el.addEventListener("error", onErr, { once: true });
      token.signal.addEventListener("abort", onAbort, { once: true });
      // "playing" lets the persistent GP player (player-service.js) know a
      // transient exercise clip has started, so it can pause the Global
      // Playlist track for the duration of the exercise (Brief 091 §2 — GP
      // ducks to exercise audio). Fired on every (re)play, including each MMU
      // clip within an exercise; the listener is idempotent.
      this.dispatchEvent(new Event("playing"));
      this.el.play().catch(onErr);
    });
  }

  pause() { this.el.pause(); }
  resume() { return this.el.play(); }
  stop() {
    if (this._currentToken) { this._currentToken.abort(); this._currentToken = null; }
    this.el.pause();
    this.el.currentTime = 0;
    // "stopped" is the GP-resume signal (Brief 091 §2): an exercise screen
    // calls services.audio.stop() in onDisconnected, which lets the persistent
    // player resume the Global Playlist track it ducked. Deliberately NOT
    // emitted on pause()/ended — individual clips end mid-exercise and must
    // NOT resume GP; only an explicit stop (screen teardown) does.
    this.dispatchEvent(new Event("stopped"));
  }

  get currentTime() { return this.el.currentTime; }
  get duration() { return this.el.duration; }
  get isPlaying() { return !this.el.paused && !this.el.ended; }
}

/** Module-level singleton, available to all screens. */
export const audio = new AudioEngine();
